import { els } from "./elements";
import { STYLES } from "../data/styles";
import { fmtTemp, fmtYeast } from "../lib/format";
import {
  autoStarter, autoYeastPct, computeStandard,
  computeSimpleSourdough, computeAdvancedSourdough,
  MAX_STARTER, HARD_MAX_STARTER,
} from "../engine";
import { render } from "./render";
import { fmtTempSys, type UnitSystem } from "../lib/units";
import type { Style, Percents, YeastType, Snapshot } from "../types";

export interface Ferment { hours: number; temp: number; expH: number; extended: boolean; }

let yeastManual = false;
export const getYeastManual = () => yeastManual;
export const setYeastManual = (b: boolean) => { yeastManual = b; };

let unitSystem: UnitSystem = "metric";
export const getUnitSystem = (): UnitSystem => unitSystem;
export const setUnitSystem = (s: UnitSystem): void => { unitSystem = s; };

const TEMP_CHIP_WORDS: Record<number, string> = {
  4: "Fridge", 21: "Room", 24: "Warm", 26: "Summer", 28: "Proof",
};

// Re-label temp chips + readout for the current system, then re-render the slip.
export function applyUnits(): void {
  els.tempChips.querySelectorAll("button").forEach((b) => {
    const c = +(b.dataset.temp ?? NaN);
    const word = TEMP_CHIP_WORDS[c] ?? "";
    const shown = unitSystem === "us" ? Math.round(c * 9 / 5 + 32) : c;
    b.textContent = `${word} ${shown}°`;
  });
  els.tempOut.textContent = fmtTempSys(+els.temp.value, unitSystem);
  els.volWarn.hidden = unitSystem !== "us";
  calc();
}

export function currentStyle(): Style {
  return STYLES.find((s) => s.id === els.style.value) || STYLES[0]!;
}

export function loadStyle(s: Style): void {
  els.countLabel.textContent = s.countLabel;
  els.unitLabel.textContent = s.unitLabel;
  els.unitWeight.value = String(s.unitWeight);
  els.hydration.value = String(s.hydration);
  els.salt.value = String(s.salt);
  els.oil.value = String(s.oil);
  els.sugar.value = String(s.sugar);
  els.styleNote.textContent = s.note;
  const hasButter = "butter" in s;
  els.butterField.hidden = !hasButter;
  els.butter.value = String(hasButter ? s.butter : 0);
  els.time.value = String(s.time);
}

export function syncYeastUI(): void {
  const isSD = els.yeast.value === "sourdough";
  els.sourdoughOpts.hidden = !isSD;
  els.yeastPctLabel.textContent = isSD ? "Starter (% of flour)" : "Yeast (% of flour)";
  if (isSD) els.yeastPct.max = String(HARD_MAX_STARTER);
  else els.yeastPct.removeAttribute("max");
}

export function refreshAutoYeast(): void {
  if (yeastManual) return;
  const hours = +els.time.value, temp = +els.temp.value;
  const pct = els.yeast.value === "sourdough"
    ? autoStarter(hours, temp).pct
    : autoYeastPct(els.yeast.value as YeastType, hours, temp);
  els.yeastPct.value = fmtYeast(pct);
}

export function setChip(container: HTMLElement, attr: string, value: number): void {
  container.querySelectorAll("button").forEach((b) => {
    b.setAttribute("aria-pressed", String(+(b.dataset[attr] ?? NaN) === value));
  });
}

export function calc(): void {
  const s = currentStyle();
  const count = Math.max(1, +els.count.value || 1);
  const unit = Math.max(1, +els.unitWeight.value || 1);
  const totalDough = count * unit;

  const p: Percents = {
    hydration: +els.hydration.value || 0,
    salt: +els.salt.value || 0,
    oil: +els.oil.value || 0,
    sugar: +els.sugar.value || 0,
    butter: els.butterField.hidden ? 0 : (+els.butter.value || 0),
    yeast: +els.yeastPct.value || 0,
    starterHyd: +els.starterHyd.value || 100,
  };

  let result, warning = "";
  if (els.yeast.value === "sourdough") {
    if (els.sdMode.value === "advanced") {
      result = computeAdvancedSourdough(totalDough, p);
      if (result.finalWater! < 0) {
        warning = "Starter carries more water than the recipe needs. Lower starter % or its hydration, or raise dough hydration.";
      }
    } else {
      result = computeSimpleSourdough(totalDough, p);
    }
  } else {
    result = computeStandard(totalDough, p);
  }

  const hours = +els.time.value, temp = +els.temp.value;
  const ferment: Ferment = { hours, temp, expH: hours, extended: false };
  if (els.yeast.value === "sourdough" && !yeastManual) {
    const r = autoStarter(hours, temp);
    if (r.capped) { ferment.expH = Math.round(r.reqHours!); ferment.extended = true; }
  }

  els.timeFlag.hidden = !ferment.extended;
  const highStarter = els.yeast.value === "sourdough" && p.yeast > MAX_STARTER;
  if (ferment.extended) {
    els.autoNote.hidden = false;
    els.autoNote.textContent =
      `Held at the ${MAX_STARTER}% starter cap. At ${fmtTemp(temp)}°C a healthy culture ` +
      `works slowly, so expect around ${ferment.expH}h to be ready, not ${hours}h. ` +
      `Cold dough wants more time, not more starter.`;
  } else if (highStarter) {
    els.autoNote.hidden = false;
    els.autoNote.textContent =
      `High inoculation (${fmtYeast(p.yeast)}% starter). Fine for a fast, same-day dough, ` +
      `but expect a quicker proof and a tangier, softer crumb, so watch it closely to avoid overproofing.`;
  } else {
    els.autoNote.hidden = true;
  }

  render(s, count, unit, totalDough, result, warning, ferment, unitSystem);
}

export function snapshot(): Snapshot {
  return {
    style: els.style.value,
    count: +els.count.value || 1,
    unitWeight: +els.unitWeight.value || 1,
    yeast: els.yeast.value as YeastType,
    sdMode: els.sdMode.value as "advanced" | "simple",
    starterHyd: +els.starterHyd.value || 100,
    time: +els.time.value,
    temp: +els.temp.value,
    yeastPct: els.yeastPct.value,
    yeastManual,
    hydration: +els.hydration.value || 0,
    salt: +els.salt.value || 0,
    oil: +els.oil.value || 0,
    sugar: +els.sugar.value || 0,
    butter: +els.butter.value || 0,
  };
}

export function applySnapshot(s: Snapshot): void {
  els.style.value = s.style;
  loadStyle(currentStyle());
  els.count.value = String(s.count);
  els.unitWeight.value = String(s.unitWeight);
  els.hydration.value = String(s.hydration);
  els.salt.value = String(s.salt);
  els.oil.value = String(s.oil);
  els.sugar.value = String(s.sugar);
  els.butter.value = String(s.butter);
  els.time.value = String(s.time);
  els.temp.value = String(s.temp);
  els.yeast.value = s.yeast;
  syncYeastUI();
  els.sdMode.value = s.sdMode;
  els.starterHyd.value = String(s.starterHyd);
  yeastManual = !!s.yeastManual;
  els.yeastPct.value = s.yeastPct;
  els.timeOut.textContent = s.time + " h";
  els.tempOut.textContent = fmtTempSys(+s.temp, unitSystem);
  setChip(els.timeChips, "time", s.time);
  setChip(els.tempChips, "temp", s.temp);
  if (!yeastManual) refreshAutoYeast();
  calc();
}
